import { useEffect, useMemo } from "react";
import { GeoJSON, MapContainer, Marker, Popup, TileLayer, useMap } from "react-leaflet";
import L from "leaflet";
import { getMalfunctionTypeLabel } from "../../utils/malfunctions";
import "leaflet/dist/leaflet.css";

function InvalidateMapSize() {
  const map = useMap();

  useEffect(() => {
    let cancelled = false;
    const frameId = requestAnimationFrame(() => {
      if (cancelled) return;

      const container = map.getContainer?.();
      const mapPane = map.getPane?.("mapPane");
      if (!container?.isConnected || !mapPane) return;

      try {
        map.invalidateSize({ pan: false });
      } catch (error) {
        console.warn("Unable to refresh map size", error);
      }
    });

    return () => {
      cancelled = true;
      cancelAnimationFrame(frameId);
    };
  }, [map]);

  return null;
}

const createDetectionIcon = (isSelected) => (
  L.divIcon({
    className: isSelected ? "camber-detection-marker-selected" : "camber-detection-marker",
    html: `
      <div style="
        width: ${isSelected ? 30 : 24}px;
        height: ${isSelected ? 30 : 24}px;
        border-radius: 50% 50% 50% 0;
        background: ${isSelected ? "#5937D6" : "#BFBFBF"};
        border: 3px solid #FFFFFF;
        box-shadow: 0 8px 18px rgba(31, 31, 31, 0.26);
        transform: rotate(-45deg);
        display: flex;
        align-items: center;
        justify-content: center;
      ">
        <div style="
          width: ${isSelected ? 9 : 7}px;
          height: ${isSelected ? 9 : 7}px;
          border-radius: 999px;
          background: #FFFFFF;
        "></div>
      </div>
    `,
    iconSize: [isSelected ? 30 : 24, isSelected ? 30 : 24],
    iconAnchor: [isSelected ? 15 : 12, isSelected ? 30 : 24],
    popupAnchor: [0, isSelected ? -30 : -24],
  })
);

function FitDetectionBounds({ routeGeom, detection }) {
  const map = useMap();

  useEffect(() => {
    if (detection?.latitude == null || detection?.longitude == null) return;
    const container = map.getContainer?.();
    if (!container?.isConnected || !map.getPane?.("mapPane")) return;

    const bounds = L.latLngBounds([]);

    if (routeGeom) {
      try {
        const routeBounds = L.geoJSON(routeGeom).getBounds();
        if (routeBounds.isValid()) bounds.extend(routeBounds);
      } catch (error) {
        console.warn("Unable to fit route geometry", error);
      }
    }

    bounds.extend([detection.latitude, detection.longitude]);

    try {
      if (bounds.isValid()) {
        map.fitBounds(bounds, { padding: [36, 36], maxZoom: 18 });
      } else {
        map.setView([detection.latitude, detection.longitude], 17);
      }
    } catch (error) {
      console.warn("Unable to fit detection map bounds", error);
    }
  }, [routeGeom, detection, map]);

  return null;
}

export default function DetectionRouteMap({
  routeGeom,
  detection,
  detections = [],
  height = "100%",
  onMarkerClick,
}) {
  const hasDetectionCoords = detection?.latitude != null && detection?.longitude != null;
  const visibleDetections = useMemo(() => {
    const withCoords = detections.filter((item) => (
      item?.latitude != null && item?.longitude != null
    ));

    if (withCoords.length > 0) return withCoords;
    return hasDetectionCoords ? [detection] : [];
  }, [detections, detection, hasDetectionCoords]);

  const selectedIcon = useMemo(() => createDetectionIcon(true), []);
  const defaultIcon = useMemo(() => createDetectionIcon(false), []);

  if (!hasDetectionCoords) {
    return (
      <div className="w-full h-full flex items-center justify-center bg-[#F4F7FB] text-[#6B7280] text-[14px]">
        No GPS coordinate available for this detection
      </div>
    );
  }

  if (!routeGeom) {
    return (
      <div className="w-full h-full flex items-center justify-center bg-[#F4F7FB] text-[#6B7280] text-[14px]">
        Route map unavailable for this video
      </div>
    );
  }

  return (
    <MapContainer
      center={[detection.latitude, detection.longitude]}
      zoom={17}
      style={{ width: "100%", height }}
      scrollWheelZoom
    >
      <InvalidateMapSize />
      <TileLayer
        url="https://tile.openstreetmap.org/{z}/{x}/{y}.png"
        attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
      />
      <GeoJSON
        data={routeGeom}
        style={{
          color: "#2563EB",
          weight: 5,
          opacity: 0.8,
        }}
      />
      {visibleDetections.map((item) => {
        const isSelected = item.id === detection?.id;

        return (
          <Marker
            key={item.id}
            position={[item.latitude, item.longitude]}
            icon={isSelected ? selectedIcon : defaultIcon}
            zIndexOffset={isSelected ? 1000 : 0}
            eventHandlers={{
              click: () => onMarkerClick?.(item),
            }}
          >
            <Popup>
              <div className="font-['Open_Sans'] text-[12px] leading-[155%] text-[#242424]">
                <div className="font-semibold">
                  {getMalfunctionTypeLabel(item)}
                </div>
                <div>{item.timecode?.toFixed?.(2)} s</div>
                <div>Frame {item.frame}</div>
              </div>
            </Popup>
          </Marker>
        );
      })}
      <FitDetectionBounds routeGeom={routeGeom} detection={detection} />
    </MapContainer>
  );
}
